import { Link } from "react-router";
import { useEffect, useState, type FormEvent } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Gift, Mail } from "lucide-react";
import { useStore } from "../../stores/rootStore";
import { getClientConfig } from "../../lib/apiClient";
import { track } from "../../lib/analytics";
import { assetUrl } from "../../lib/assets";
import {
	clearPendingReferralCode,
	normalizeReferralCode,
	peekPendingReferralCode,
	setPendingReferralCode,
} from "../../lib/referralCapture";
import { Spinner } from "../ui/Spinner";
import { AppleSignInButton } from "./AppleSignInButton";
import { GoogleSignInButton } from "./GoogleSignInButton";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RESEND_SECONDS = 60;
/** Same alphabet as normalizeReferralCode — filtered as the visitor types instead of on submit. */
const REFERRAL_CHAR_RE = /[^ABCDEFGHJKLMNPQRSTUVWXYZ23456789]/g;
const REFERRAL_CODE_LENGTH = 6;

interface AuthFormProps {
	showTitle?: boolean;
	showLogo?: boolean;
}

/** Email + code sign-in with third-party buttons and terms/privacy links. Shared by AuthModal and LoginPage. */
export const AuthForm = observer(function AuthForm({
	showTitle = true,
	showLogo = true,
}: AuthFormProps) {
	const { auth, ui } = useStore();
	const { t } = useTranslation();
	const config = getClientConfig();
	const hasThirdPartyAuth = Boolean(config.googleClientId || config.appleClientId);
	const [step, setStep] = useState<"email" | "code">("email");
	const [email, setEmail] = useState("");
	const [code, setCode] = useState("");
	const [resendLeft, setResendLeft] = useState(0);
	// Seeded from the code an invite link captured, so the visitor can see and correct what will be
	// submitted; every sign-in path reads it back out of storage, not from here.
	const [inviteCode, setInviteCode] = useState(() => peekPendingReferralCode() ?? "");
	const [inviteEditing, setInviteEditing] = useState(false);
	const inviteValid = Boolean(normalizeReferralCode(inviteCode));

	useEffect(() => {
		if (peekPendingReferralCode()) ui.setAuthMode("signUp");
	}, [ui]);

	useEffect(() => {
		if (resendLeft <= 0) return;
		const timer = window.setInterval(() => setResendLeft((v) => (v > 0 ? v - 1 : 0)), 1000);
		return () => window.clearInterval(timer);
	}, [resendLeft]);

	async function handleSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (step === "email") {
			const normalized = email.trim().toLowerCase();
			if (!EMAIL_RE.test(normalized)) return;
			const sent = await auth.requestEmailCode(normalized);
			if (sent) {
				setEmail(normalized);
				setCode("");
				setResendLeft(RESEND_SECONDS);
				setStep("code");
			}
			return;
		}
		if (code.length === 6) await auth.verifyEmailCode(email, code);
	}

	function handleCodeChange(value: string) {
		const digits = value.replace(/\D/g, "").slice(0, 6);
		setCode(digits);
		if (digits.length === 6 && !auth.submitting) void auth.verifyEmailCode(email, digits);
	}

	function handleInviteChange(value: string) {
		const next = value.toUpperCase().replace(REFERRAL_CHAR_RE, "").slice(0, REFERRAL_CODE_LENGTH);
		setInviteCode(next);
		if (normalizeReferralCode(next)) {
			setPendingReferralCode(next);
			track("referral_captured", { source: "manual_entry" });
		} else {
			// Half-typed or cleared: drop the stored code so sign-in can't submit a stale one.
			clearPendingReferralCode();
		}
	}

	async function handleResend() {
		if (auth.submitting || resendLeft > 0) return;
		const sent = await auth.requestEmailCode(email);
		if (sent) {
			setCode("");
			setResendLeft(RESEND_SECONDS);
		}
	}

	function switchMode(mode: "signIn" | "signUp") {
		if (mode === ui.authMode) return;
		ui.setAuthMode(mode);
		setStep("email");
		setCode("");
		setInviteEditing(false);
		auth.clearFeedback();
		if (mode === "signIn") {
			clearPendingReferralCode();
			setInviteCode("");
		}
	}

	return (
		<div className="flex flex-col gap-4">
			<div className={showLogo ? "flex items-center gap-3" : "text-center"}>
				{showLogo ? (
					<div className="grid size-11 shrink-0 place-items-center rounded-xl border border-black/10 bg-white shadow-sm">
						<img
							src={assetUrl("brand/haha-mascot.png")}
							alt=""
							draggable={false}
							className="size-8 object-contain"
						/>
					</div>
				) : null}
				<div className="min-w-0">
					{showTitle ? (
						<h1 className="text-xl font-bold leading-6">
							{t(ui.authMode === "signUp" ? "auth.signUpTitle" : "auth.signInTitle")}
						</h1>
					) : null}
					<p className={showTitle ? "mt-1 text-sm text-muted" : "text-sm text-muted"}>
						{t(ui.authMode === "signUp" ? "auth.haveAccount" : "auth.newToMuhaha")}{" "}
						<button
							type="button"
							onClick={() => switchMode(ui.authMode === "signUp" ? "signIn" : "signUp")}
							className="cursor-pointer text-muted underline underline-offset-2 transition hover:text-text"
						>
							{t(ui.authMode === "signUp" ? "auth.signIn" : "auth.signUp")}
						</button>
					</p>
				</div>
			</div>

			{step === "email" && hasThirdPartyAuth ? (
				<>
					<div className="flex flex-col gap-2">
						<GoogleSignInButton />
						<AppleSignInButton />
					</div>
					<div className="flex items-center gap-3 text-xs text-faint">
						<span className="h-px flex-1 bg-line" />
						{t("auth.or")}
						<span className="h-px flex-1 bg-line" />
					</div>
				</>
			) : null}

			<form onSubmit={handleSubmit} className="flex flex-col gap-3">
				{step === "email" ? (
					<label className="flex flex-col gap-1.5">
						<span className="text-xs font-medium text-muted">{t("auth.emailLabel")}</span>
						<div className="relative">
							<Mail
								size={16}
								className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-faint"
							/>
							<input
								type="email"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								placeholder="you@example.com"
								autoFocus
								className="field pl-9"
							/>
						</div>
					</label>
				) : (
					<div className="flex flex-col gap-2">
						<button
							type="button"
							onClick={() => {
								setStep("email");
								setCode("");
								auth.clearFeedback();
							}}
							className="flex w-fit items-center gap-1 text-xs text-muted hover:text-text"
						>
							<ArrowLeft size={14} /> {t("auth.changeEmail")}
						</button>
						<label className="flex flex-col gap-1.5">
							<span className="text-xs font-medium text-muted">
								{t("auth.codeSentTo")} <b className="text-text">{email}</b>
							</span>
							<input
								inputMode="numeric"
								value={code}
								onChange={(e) => handleCodeChange(e.target.value)}
								placeholder={t("auth.codePlaceholder")}
								autoFocus
								className="field text-center text-lg tracking-[0.5em]"
							/>
						</label>
						<button
							type="button"
							onClick={handleResend}
							disabled={resendLeft > 0 || auth.submitting}
							className="w-fit text-xs text-brand disabled:text-faint"
						>
							{resendLeft > 0 ? t("auth.resendIn", { s: resendLeft }) : t("auth.resend")}
						</button>
					</div>
				)}

				{auth.error && <p className="text-xs text-danger">{auth.error}</p>}
				{auth.notice && !auth.error && <p className="text-xs text-teal">{auth.notice}</p>}

				<button type="submit" disabled={auth.submitting} className="btn-primary w-full">
					{auth.submitting && <Spinner size={16} />}
					{step === "email" ? t("auth.continueWithEmail") : t("auth.verifyAndContinue")}
				</button>
			</form>

			{step === "email" && ui.authMode === "signUp" ? (
				/* Referral code is a sign-up detail, so keep it out of the sign-in flow. */
				inviteEditing ? (
				<label className="flex flex-col gap-1.5">
					<span className="text-xs font-medium text-muted">{t("auth.inviteCodeLabel")}</span>
					<input
						value={inviteCode}
						onChange={(e) => handleInviteChange(e.target.value)}
						placeholder={t("auth.inviteCodePlaceholder")}
						autoFocus
						className="field text-center tracking-[0.3em]"
					/>
					{inviteValid ? (
						<span className="text-xs text-teal">{t("auth.inviteCodeValid")}</span>
					) : (
						inviteCode.length > 0 && (
							<span className="text-xs text-danger">{t("auth.inviteCodeInvalid")}</span>
						)
					)}
				</label>
			) : inviteValid ? (
				<div className="flex items-center gap-2 rounded-xl border border-line bg-surface-2 px-3 py-2 text-xs">
					<Gift size={14} className="shrink-0 text-brand" />
					<span className="min-w-0 flex-1">
						{t("auth.inviteCodeApplied")} <b className="tracking-wider">{inviteCode}</b>
					</span>
					<button
						type="button"
						onClick={() => setInviteEditing(true)}
						className="shrink-0 text-brand hover:underline"
					>
						{t("auth.inviteCodeChange")}
					</button>
				</div>
			) : (
				<button
					type="button"
					onClick={() => setInviteEditing(true)}
					className="w-fit text-xs text-muted hover:text-text"
				>
					{t("auth.inviteCodeToggle")}
				</button>
			)) : null}

			<p className="text-center text-[11px] leading-relaxed text-faint">
				{t("auth.agreePrefix")} {" "}
				<Link to="/tos" className="cursor-pointer text-muted underline underline-offset-2 transition hover:text-text">
					{t("auth.termsOfService")}
				</Link>{" "}
				{t("auth.agreeJoiner")} {" "}
				<Link to="/privacy" className="cursor-pointer text-muted underline underline-offset-2 transition hover:text-text">
					{t("auth.privacyPolicy")}
				</Link>
			</p>
		</div>
	);
});
